import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { localeData } from 'localization/localization-data';
import { CopyTranslations, Currency } from 'localization/localization-types';
import { Country, CountryEnum } from 'types/enums';
import { bankersRound } from 'utils/functions';

import { useAuth } from './auth-context';

type DiscountType = 'PERCENTAGE' | 'FIXED_AMOUNT';

interface ComputedInvoiceItemValue {
  total: number;
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
}

interface ComputedInvoiceSummary extends ComputedInvoiceItemValue {
  isDiscounted: boolean;
}

interface InvoiceItem {
  itemPrice: number;
  quantity: number;
  taxRateValue: number;
  discountValue?: number;
  discountType?: DiscountType;
}

interface UseLocalizationReturn {
  country: Country;
  isTaxInclusive: boolean;
  formatCurrency: (value: number, adjustments?: Intl.NumberFormatOptions) => string;
  dateFormat: string;
  dateTimeFormat: string;
  timeFormat: string;
  copy: CopyTranslations;
  calculateInvoiceItemValues: (invoiceItem: InvoiceItem) => ComputedInvoiceItemValue;
  calculateInvoiceSummary: (invoiceItems: InvoiceItem[]) => ComputedInvoiceSummary;
}

const computeDiscountAmount = (
  nonDiscountedTotal: number,
  discountValue: number | undefined,
  discountType: DiscountType | undefined,
): number => {
  if (discountValue === undefined || discountType === undefined) return 0;
  if (discountType === 'PERCENTAGE') {
    return nonDiscountedTotal * discountValue;
  }
  return Math.min(nonDiscountedTotal, discountValue);
};

const computeInvoiceItemValues = (
  invoiceItem: InvoiceItem,
  isTaxInclusive: boolean,
): ComputedInvoiceItemValue => {
  const { itemPrice, quantity, taxRateValue, discountValue, discountType } = invoiceItem;
  const taxRate = (taxRateValue ?? 0) / 100;

  if (isTaxInclusive) {
    const nonDiscountedTotalPrice = (itemPrice ?? 0) * quantity;
    const discountAmount = computeDiscountAmount(nonDiscountedTotalPrice, discountValue, discountType);
    const total = Math.max(0, nonDiscountedTotalPrice - discountAmount);
    const taxAmount = bankersRound(total - total / (1 + taxRate));
    const subtotal = total - taxAmount;
    return { total, subtotal, taxAmount, discountAmount };
  }
  const nonDiscountedPretaxPrice = (itemPrice ?? 0) * quantity;
  const discountAmount = computeDiscountAmount(nonDiscountedPretaxPrice, discountValue, discountType);
  const subtotal = Math.max(0, nonDiscountedPretaxPrice - discountAmount);
  const taxAmount = bankersRound(subtotal * taxRate);
  const total = subtotal + taxAmount;
  return { total, subtotal, taxAmount, discountAmount };
};

const computeInvoiceSummary = (
  invoiceItems: InvoiceItem[],
  computeInvoiceItemValuesFn: (invoiceItem: InvoiceItem) => ComputedInvoiceItemValue,
): ComputedInvoiceSummary => {
  const result = invoiceItems.reduce(
    (acc, invoiceItem) => {
      const { total, subtotal, taxAmount, discountAmount } = computeInvoiceItemValuesFn(invoiceItem);
      return {
        total: acc.total + total,
        subtotal: acc.subtotal + subtotal,
        taxAmount: acc.taxAmount + taxAmount,
        discountAmount: acc.discountAmount + discountAmount,
      };
    },
    { total: 0, subtotal: 0, taxAmount: 0, discountAmount: 0 },
  );

  return {
    ...result,
    isDiscounted: result.discountAmount > 0,
  };
};

const getCurrencyFormatter = (
  currency: Currency,
  adjustments: Intl.NumberFormatOptions = {},
): Intl.NumberFormat => {
  return new Intl.NumberFormat(currency.locale, {
    style: 'currency',
    currency: currency.currency,
    ...adjustments,
  });
};

const buildLocalizationValue = (country: Country, isTaxInclusive: boolean): UseLocalizationReturn => {
  const locale = localeData[country];

  const calculateInvoiceItemValues = (invoiceItem: InvoiceItem): ComputedInvoiceItemValue =>
    computeInvoiceItemValues(invoiceItem, isTaxInclusive);

  const calculateInvoiceSummary = (invoiceItems: InvoiceItem[]): ComputedInvoiceSummary =>
    computeInvoiceSummary(invoiceItems, calculateInvoiceItemValues);

  return {
    country,
    isTaxInclusive,
    formatCurrency: (value, adjustments = {}) =>
      getCurrencyFormatter(locale.currency, adjustments).format(value),
    dateFormat: locale.dateFormat,
    dateTimeFormat: locale.dateTimeFormat,
    timeFormat: locale.timeFormat,
    copy: locale.copy,
    calculateInvoiceItemValues,
    calculateInvoiceSummary,
  };
};

const LocalizationContext = createContext<UseLocalizationReturn>(
  buildLocalizationValue(CountryEnum.USA, false),
);

const LocalizationProvider = ({ children }: { children: React.ReactNode }): any => {
  const { isLoading, session } = useAuth();
  const [gymCountry, setGymCountry] = useState<Country>(CountryEnum.USA);

  useEffect(() => {
    if (!isLoading && !!session?.gym) {
      setGymCountry(session.gym.country);
    }
  }, [isLoading, session?.gym]);

  const isTaxInclusive = !!session?.gym?.isTaxInclusive;

  const contextValue = useMemo(
    () => buildLocalizationValue(gymCountry, isTaxInclusive),
    [gymCountry, isTaxInclusive],
  );

  return <LocalizationContext.Provider value={contextValue}>{children}</LocalizationContext.Provider>;
};

export const useLocalization = (
  overrideCountry?: Country,
  {
    isTaxInclusive: overrideIsTaxInclusive,
  }: {
    isTaxInclusive?: boolean;
  } = {},
): UseLocalizationReturn => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within LocalizationProvider');
  }

  if (!overrideCountry && overrideIsTaxInclusive === undefined) {
    return context;
  }

  const effectiveCountry = overrideCountry ?? context.country;
  const effectiveIsTaxInclusive = overrideIsTaxInclusive ?? context.isTaxInclusive;

  return buildLocalizationValue(effectiveCountry, effectiveIsTaxInclusive);
};

export default LocalizationProvider;
